import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gem } from "lucide-react";
import { api, imgProps } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { FilmPlayer } from "@/components/FilmPlayer";

const HERO_SLIDES = [
  {
    src: "/assets/brand/hero-campaign-1.jpg",
    alt: "Model in a peach dupatta wearing a Merakee emerald and diamond necklace, earrings, bracelet and ring",
    href: "/collections?gemstone=emerald",
    label: "Emeralds",
  },
  {
    src: "/assets/brand/hero-campaign-2.jpg",
    alt: "Merakee ruby and diamond tassel necklace laid on white silk",
    href: "/collections?gemstone=ruby",
    label: "Rubies",
  },
  {
    src: "/assets/brand/hero-campaign-3.jpg",
    alt: "Model in a white embroidered gown wearing a Merakee ruby and diamond necklace and earrings",
    href: "/collections?gemstone=ruby",
    label: "Rubies",
  },
  {
    src: "/assets/brand/hero-campaign-4.jpg",
    alt: "Three Merakee diamond and emerald necklaces displayed on black velvet busts",
    href: "/collections?category=necklaces&gemstone=emerald",
    label: "Emerald Necklaces",
  },
];
const STORY = "/assets/brand/story.jpg";

const COLLECTION_TILES = [
  {
    key: "polki-sets",
    label: "Polki Sets",
    note: "Uncut diamonds, jadau craft",
    image: "/assets/brand/collection-polki.jpg",
  },
  {
    key: "necklaces",
    label: "Necklaces",
    note: "Solitaires to cascades",
    image: "/assets/brand/set4-necklace.jpg",
  },
  {
    key: "bracelets",
    label: "Bracelets",
    note: "Kadas to tennis lines",
    image: "/assets/brand/set2-bangle.jpg",
  },
  {
    key: "rings",
    label: "Rings",
    note: "Quiet statements",
    image: "/assets/brand/ring-bahar.jpg",
  },
  {
    key: "earrings",
    label: "Earrings",
    note: "Emeralds & pavé light",
    image: "/assets/brand/earring-jharna.jpg",
  },
];

const MARQUEE_ITEMS = [
  "Real Diamonds",
  "Polki Heritage",
  "In-House Design",
  "Private Consultations",
  "Handcrafted in India",
];

const MANIFESTO = [
  [
    "01",
    "Authentic Diamonds",
    "Real, natural diamonds — never imitations, never described as more than they are. Every stone is chosen for character before carat.",
  ],
  [
    "02",
    "Polki, Reimagined",
    "Our design work happens in-house, carrying the uncut-diamond tradition of jadau into pieces that belong as much to today as to ceremony.",
  ],
  [
    "03",
    "Craftsmanship",
    "Each piece passes through many hands — setting, enamelling, finishing — and is judged at every stage by eye, not by quota.",
  ],
];

const TESTIMONIALS = [
  {
    quote:
      "The polki set I wore at my wedding was the single most complimented thing of the evening. Merakee made me feel like a maharani.",
    name: "Ananya R.",
    detail: "Rajkumari Polki Bridal Set",
  },
  {
    quote:
      "I live abroad and bought entirely over video consultations. The diamond was exactly as promised — certified, stunning, unforgettable.",
    name: "Priya & Karan M.",
    detail: "Aurora Solitaire Necklace",
  },
  {
    quote:
      "Their concierge understood my budget and never pushed. My tennis bracelet is now my everyday piece of quiet luxury.",
    name: "Shreya D.",
    detail: "Aria Tennis Bracelet",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [articles, setArticles] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
    api.get("/journal").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section
        className="relative mt-20 h-[calc(100svh-5rem)] overflow-hidden bg-[#262019] sm:aspect-video sm:h-auto sm:max-h-[calc(100svh-5rem)] sm:w-full"
        data-testid="hero-section"
      >
        {HERO_SLIDES.map((s, i) => (
          <Link
            key={s.src}
            to={s.href}
            aria-label={`Explore ${s.label}`}
            data-testid={`hero-slide-link-${i}`}
            className={`hero-slide absolute inset-0 block ${i === slide ? "is-active" : "is-inactive pointer-events-none"}`}
            tabIndex={i === slide ? 0 : -1}
          >
            <img
              src={s.src}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl brightness-[0.7]"
            />
            <img
              {...imgProps(s.src, "100vw")}
              alt={s.alt}
              className="hero-kenburns absolute inset-0 h-full w-full object-contain object-center"
            />
          </Link>
        ))}
        <Link
          to={HERO_SLIDES[slide].href}
          data-testid="hero-explore-link"
          className="absolute bottom-[6.25rem] left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap border border-white/60 bg-white/10 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur-md transition-colors duration-300 hover:bg-white/25 sm:bottom-6 sm:left-auto sm:right-24 sm:translate-x-0 sm:text-xs lg:right-28"
        >
          Explore {HERO_SLIDES[slide].label}
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Link>
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[#262019]/35 to-transparent" />
        <p
          data-testid="hero-brand-overlay"
          className="absolute inset-x-0 bottom-16 z-10 text-center text-[10px] font-light uppercase tracking-[0.3em] text-white [text-shadow:0_1px_16px_rgba(0,0,0,0.7)] sm:text-sm sm:tracking-[0.5em]"
        >
          Merakee Fine Jewellery
        </p>
        <div className="absolute bottom-6 left-6 z-20 flex lg:left-10" data-testid="hero-slide-dots">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Show banner ${i + 1}`}
              className="flex h-9 w-9 items-center justify-center"
            >
              <span
                className={`h-2.5 w-2.5 rounded-full border border-white/70 shadow-sm transition-colors duration-300 ${
                  i === slide ? "bg-white" : "bg-transparent hover:bg-white/50"
                }`}
              />
            </button>
          ))}
        </div>
      </section>

      <section className="overflow-hidden border-y border-[#A58343]/25 bg-[#FAF7F1] py-5" data-testid="marquee-strip">
        <div className="marquee-track">
          {[0, 1].map((n) => (
            <div key={n} className="flex shrink-0 items-center" aria-hidden={n === 1}>
              {MARQUEE_ITEMS.map((t) => (
                <span key={t} className="mx-8 flex items-center gap-8 whitespace-nowrap text-xs uppercase tracking-[0.35em] text-[#8C6D33]">
                  {t} <span className="text-[#A58343]">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32" data-testid="collections-section">
        <Reveal>
          <p className="overline">Explore the Collection</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-xl font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl">
              Four rooms of <span className="italic text-[#A58343]">light</span>
            </h2>
            <Link to="/collections" data-testid="view-all-collections" className="group flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#8C6D33] transition-colors duration-300 hover:text-[#A58343]">
              View all pieces
              <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-16 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {COLLECTION_TILES.map((tile, i) => (
            <Reveal key={tile.key} delay={0.08 * i}>
              <Link to={`/collections?category=${tile.key}`} data-testid={`collection-tile-${tile.key}`} className="group block">
                <div className="relative overflow-hidden bg-[#262019]">
                  <img {...imgProps(tile.image, "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw")} alt={tile.label} loading="lazy" className="block h-auto w-full" />
                  <div className="absolute inset-0 bg-[#262019]/0 transition-colors duration-500 group-hover:bg-[#262019]/15" />
                  <p className="absolute left-5 top-5 font-serif text-sm italic text-[#FAF7F1] [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]">0{i + 1}</p>
                </div>
                <div className="mt-5 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#7C7163]">{tile.note}</p>
                    <h3 className="mt-2 font-serif text-2xl font-light text-[#262019] transition-colors duration-300 group-hover:text-[#A58343]">{tile.label}</h3>
                  </div>
                  <p className="flex items-center gap-2 pb-1 text-[10px] uppercase tracking-[0.25em] text-[#8C6D33] opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    Explore <ArrowRight size={12} strokeWidth={1.5} />
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="grain border-y border-[#A58343]/20 bg-[#EFE8DC]" data-testid="featured-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
          <Reveal>
            <p className="overline">From the Kolkata Shoot</p>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl">
              Every photograph is the <span className="italic text-[#A58343]">piece itself</span>
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#7C7163]">
              No renders, no stand-ins. What you see is what sits in the atelier — and more pieces are being photographed now.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-products-grid">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={0.1 * i}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32" data-testid="manifesto-section">
        <div className="grid items-start gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="lg:sticky lg:top-32">
              <p className="overline">The House</p>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl">
                Jewellery, <span className="italic text-[#A58343]">considered</span>
              </h2>
              <div className="relative mt-12">
                <img {...imgProps(STORY, "(max-width: 1024px) 100vw, 50vw")} alt="A Merakee pendant resting on ivory silk" loading="lazy" className="block h-auto w-full" />
              </div>
            </div>
          </Reveal>
          <div className="lg:pt-24">
            {MANIFESTO.map(([n, t, d], i) => (
              <Reveal key={n} delay={0.1 * i}>
                <div className={`flex gap-8 border-t border-[#A58343]/25 py-12 ${i === MANIFESTO.length - 1 ? "border-b" : ""}`}>
                  <span className="font-serif text-5xl font-light italic leading-none text-[#A58343]/70">{n}</span>
                  <div>
                    <h3 className="font-serif text-2xl font-medium text-[#262019]">{t}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-[#7C7163]">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link to="/about" className="btn-gold mt-10" data-testid="manifesto-read-more">
                Our Full Story
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="grain border-y border-[#A58343]/20 bg-[#EFE8DC]" data-testid="heritage-section">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-28">
          <Reveal>
            <p className="overline">Heritage, Reimagined</p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl">
              Old hands, <span className="italic text-[#A58343]">new eyes</span>
            </h2>
            <p className="mt-8 text-base leading-relaxed text-[#7C7163]">
              South Asian jewellery has always been a conversation between generations. Polki
              diamonds once set for royal courts now rest against silk, linen and evening black.
              Merakee works inside that tradition rather than around it — traditional forms,
              re-proportioned for the present, and made to move from one generation's keeping
              to the next.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32" data-testid="film-section">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-serif text-5xl font-medium leading-tight tracking-tight text-[#262019] sm:text-6xl lg:text-7xl" data-testid="film-heading">
              The Merakee <span className="italic text-[#A58343]">Film</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <FilmPlayer />
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 text-center">
            <Link to="/consultation" className="btn-gold" data-testid="film-book-consultation">
              Book a Private Consultation
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="grain border-y border-[#A58343]/20 bg-[#F4EEE3]" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
          <Reveal>
            <p className="overline text-center">Words From Our Clients</p>
          </Reveal>
          <div className="mt-16 grid gap-16 lg:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={0.12 * i}>
                <figure className="border-l border-[#A58343]/30 pl-8">
                  <blockquote className="font-serif text-xl font-light italic leading-relaxed text-[#262019]">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-6">
                    <p className="text-sm tracking-[0.15em] text-[#8C6D33]">{t.name}</p>
                    <p className="mt-1 text-xs text-[#7C7163]">{t.detail}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {articles.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32" data-testid="journal-section">
          <Reveal>
            <p className="overline">From the Journal</p>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
              <h2 className="max-w-xl font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl">
                Notes on <span className="italic text-[#A58343]">craft & care</span>
              </h2>
              <Link to="/journal" data-testid="view-all-journal" className="group flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#8C6D33] transition-colors duration-300 hover:text-[#A58343]">
                All stories
                <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-16 grid gap-12 md:grid-cols-3">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={0.1 * i}>
                <Link to={`/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`} className="group block">
                  <div className="overflow-hidden bg-[#F4EEE3]">
                    <img {...imgProps(a.image, "(max-width: 768px) 100vw, 33vw")} alt={a.title} loading="lazy" className="block h-auto w-full transition-opacity duration-500 group-hover:opacity-90" />
                  </div>
                  <p className="overline mt-6 !text-[10px]">{a.read_time}</p>
                  <h3 className="mt-3 font-serif text-2xl font-medium leading-snug text-[#262019] transition-colors duration-300 group-hover:text-[#A58343]">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#7C7163]">{a.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="grain border-t border-[#A58343]/20 bg-[#EFE8DC]" data-testid="cta-section">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center lg:py-32">
          <Reveal>
            <p className="overline">Private Consultations</p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl">
              Every piece deserves a conversation
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#262019]/70">
              No showroom pressure, no obligation. An unhurried look at the piece, its
              materials and its documentation — and honest guidance on whether it belongs with you.
            </p>
            <Link to="/consultation" className="btn-gold-solid mt-10" data-testid="cta-book-consultation">
              Book a Private Consultation
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
